import { useIsFocused } from '@react-navigation/native';
import { hasExpiredPlanAtom, presentB2CPaidModalAtom, userPlanAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { Separator } from 'app/design/primitives/text-divider';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useHasExpired } from 'app/hooks/use-has-expired';
import { useNavigation, useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { useAllGroups } from 'app/network/groups';
import { isFreeTrialPlanCode, useMyActivePlans, useStopPlanRecurrence } from 'app/network/plan';
import { MyPlanRedesign } from './my-plan-redesign';
import { useReferralPrizeAvailable } from 'app/network/promotions';
import { useUserCurrency } from 'app/network/user';
import { PlanRecurrenceEnum } from 'app/schemas/plan';
import ActivationModal, { useActivationModal } from 'app/screens/shared/activation-modal';
import { LPCEvent, useIsEventActive } from 'app/services/events';
import type { NavigationScreen } from 'app/types/navigation';
import type { Plan } from 'app/types/plan';
import { isNotWhiteLabel } from 'app/utils/helpers';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { theme } from './my-plan/shared';
import { ActivePlanDetails } from './my-plan/active-plan-details';
import { ExtraGroups } from './my-plan/extra-groups';
import { ExpiredPlan } from './my-plan/expired-plan';
import { PlanActions } from './my-plan/plan-actions';
import { ReferralProgram } from './my-plan/referral-program';
import { PlanBenefits } from './my-plan/plan-benefits';
import { StopRecurrenceModal } from './my-plan/stop-recurrence-modal';

function getMyPlanPresentation({
  activePlan,
  hasExpiredPlan,
  isLargeUI,
  showActivationButton,
}: {
  activePlan: Plan | undefined;
  hasExpiredPlan: boolean;
  isLargeUI: boolean;
  showActivationButton: boolean;
}) {
  const isOnFreeTrial = isFreeTrialPlanCode(activePlan?.code.code);
  const isB2CWithoutRecurrence =
    !isOnFreeTrial && !activePlan?.code.group && activePlan?.recurrence === PlanRecurrenceEnum.no;
  const isB2CWithRecurrence =
    !isOnFreeTrial && !activePlan?.code.group && activePlan?.recurrence !== PlanRecurrenceEnum.no;

  return {
    activePlanVariant: {
      billing: isB2CWithRecurrence ? 'recurring' : isB2CWithoutRecurrence ? 'recurrence-available' : 'other',
      layout: isLargeUI ? 'large' : 'compact',
      lifecycle: isOnFreeTrial ? 'free-trial' : 'standard',
    } as const,
    isB2CWithRecurrence,
    isOnFreeTrial,
    showActivePlan: Boolean(activePlan && !hasExpiredPlan),
    showAddCodeButton: showActivationButton && !activePlan?.code.group,
    showCurrentPlan: !hasExpiredPlan,
    showExpiredPlan: hasExpiredPlan,
  };
}

const MyPlanScreenComponent: NavigationScreen<'my-plan'> = ({ route }) => {
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const {
    data: userPlans,
    refetch: refetchActivePlans,
    isPending: isPendingActivePlans,
  } = useMyActivePlans({ throwOnError: true });
  const { data: userGroups, isPending: isPendingUserGroups } = useAllGroups();
  const { t } = useScopedTranslation();
  const isDragonEventActive = useIsEventActive(LPCEvent.Dragon2024);
  const userCurrency = useUserCurrency();
  const storedUserPlans = useAtomValue(userPlanAtom);

  const referralPrizeAvailable = useReferralPrizeAvailable();

  const [isStopRecurrenceModalOpen, setIsStopRecurrenceModalOpen] = useState(false);

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(isNotWhiteLabel() ? 'design.manageTitle' : 'title')}
        </Text>
      ),
    },
    false,
  );
  const setPageTitle = usePageTitle('my-plan');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  useEffect(() => {
    async function fetchActivePlan() {
      await refetchActivePlans();
    }
    void fetchActivePlan();
  }, [isFocused, refetchActivePlans]);

  const hasExpiredPlan = useAtomValue(hasExpiredPlanAtom);

  const activePlan = userPlans?.[0] ?? storedUserPlans?.[0];
  const activePlanHasExpired = useHasExpired(activePlan?.expiresAt, hasExpiredPlan);

  const extraGroups = userGroups?.filter((group) => activePlan?.code.group?.id !== group.id);

  const { isActivationModalOpen, openActivationModal, showActivationButton } = useActivationModal();

  const sawPaymentSuccessModal = useRef(false);
  const presentB2cPaidModal = useSetAtom(presentB2CPaidModalAtom);
  useEffect(() => {
    if (route.params?.paymentSuccess && !sawPaymentSuccessModal.current) {
      sawPaymentSuccessModal.current = true;
      presentB2cPaidModal(true);
    }
  }, [presentB2cPaidModal, route.params?.paymentSuccess]);

  const { mutate: doStopRecurrence, isPending: isStoppingRecurrence } = useStopPlanRecurrence();

  const onStopRecurrenceConfirmed = () => {
    doStopRecurrence(activePlan!.id, {
      onSuccess: () => {
        setIsStopRecurrenceModalOpen(false);
      },
    });
  };

  const defaultPaddingStyle = usePagePadding(false);
  const { isLargeUI } = useUISize();
  const {
    activePlanVariant,
    isB2CWithRecurrence,
    isOnFreeTrial,
    showActivePlan,
    showAddCodeButton,
    showCurrentPlan,
    showExpiredPlan,
  } = getMyPlanPresentation({
    activePlan,
    hasExpiredPlan,
    isLargeUI,
    showActivationButton: Boolean(showActivationButton),
  });

  useRegisterLoading(isPendingActivePlans, 'MyPlanScreen - fetching active plans');
  useRegisterLoading(isPendingUserGroups, 'MyPlanScreen - fetching user groups');

  if (isNotWhiteLabel()) {
    return (
      <MyPlanRedesign
        activePlan={activePlan}
        hasExpiredPlan={activePlanHasExpired}
        isActivationModalOpen={isActivationModalOpen[0]}
        isStopRecurrenceModalOpen={isStopRecurrenceModalOpen}
        isStoppingRecurrence={isStoppingRecurrence}
        onActivate={openActivationModal}
        onCloseStopRecurrenceModal={() => setIsStopRecurrenceModalOpen(false)}
        onConfirmStopRecurrence={onStopRecurrenceConfirmed}
        onOpenStopRecurrenceModal={() => setIsStopRecurrenceModalOpen(true)}
        onSubscribe={() => navigation.navigate('payment', undefined, { pop: true })}
        renderActivationModal={<ActivationModal isOpenState={isActivationModalOpen} />}
        userCurrency={userCurrency}
        showActivationButton={Boolean(showActivationButton)}
      />
    );
  }

  return (
    <Page paddingStyle={{ ...defaultPaddingStyle, paddingLeft: 0, paddingRight: 0 }}>
      <Column gap="spacing.general.x4" style={{ paddingHorizontal: defaultPaddingStyle.paddingLeft }}>
        {showActivePlan ? (
          <ActivePlanDetails
            activePlan={activePlan!}
            onStopRecurrence={() => setIsStopRecurrenceModalOpen(true)}
            userCurrency={userCurrency}
            variant={activePlanVariant}
          />
        ) : null}

        {showCurrentPlan ? <ExtraGroups groups={extraGroups} /> : null}

        {showExpiredPlan ? <ExpiredPlan {...{ isOnFreeTrial }} /> : null}

        <PlanActions
          {...{ activePlan }}
          isDragonEventActive={isDragonEventActive()}
          onAddCode={openActivationModal}
          showAddCodeButton={showAddCodeButton}
        />
      </Column>

      {isNotWhiteLabel() ? (
        <Separator
          height={10}
          style={{
            marginTop: theme.spacing['spacing.general.x6'],
            marginBottom: theme.spacing['spacing.general.x4'],
          }}
        />
      ) : null}

      <ReferralProgram available={referralPrizeAvailable} />

      {referralPrizeAvailable && isNotWhiteLabel() ? (
        <Separator
          height={10}
          style={{
            marginTop: theme.spacing['spacing.general.x6'],
            marginBottom: theme.spacing['spacing.general.x4'],
          }}
        />
      ) : null}

      <Column gap="spacing.general.x4" style={{ paddingHorizontal: defaultPaddingStyle.paddingLeft }}>
        <PlanBenefits {...{ activePlan, isB2CWithRecurrence }} isDragonEventActive={isDragonEventActive()} />
      </Column>

      <ActivationModal isOpenState={isActivationModalOpen} />

      <StopRecurrenceModal
        activePlan={activePlan}
        isOpen={isStopRecurrenceModalOpen}
        isPending={isStoppingRecurrence}
        onClose={() => setIsStopRecurrenceModalOpen(false)}
        onConfirm={onStopRecurrenceConfirmed}
      />
    </Page>
  );
};

const MyPlanScreen: NavigationScreen<'my-plan'> = withOptionalMetrics(
  'MyPlanScreen',
  withErrorBoundary(withDashboardLayout(MyPlanScreenComponent)),
);

export { MyPlanScreen };

export { buildTextPlan } from './my-plan-utils';
